/**
 * The New card dialog — DESIGN.md §6.3, §6.15, §6.16, §13.5.
 *
 * One dialog for the whole app: the project header, the `c` hotkey and the ⌘K
 * palette all open *this* (see lib/ui-events.ts), which is why it takes a
 * target rather than living inside a route.
 *
 * Three rules shape it, all from the Long-Day Review:
 *  - it is a real `<form>`, so Enter submits from any field (KEYS-10);
 *  - it carries the fields a handover actually needs — assignee, status,
 *    description — instead of sending the user to the panel afterwards
 *    (DAILY-15);
 *  - "Create and add another" keeps it open and refocuses the title, so five
 *    cards in a row never need the mouse (VIEW-6).
 *
 * Creating is not opening: the toast offers the new card, nothing steals the
 * screen.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Priority } from '@slate/shared';
import { isFieldHidden, isFieldRequired, textDoc } from '@slate/shared';
import { Button } from '@/components/ui/button';
import { ColorDot, Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreateTask, useSimilarTasks } from '@/features/tasks/api';
import { useProject, useProjects } from '@/features/projects/api';
import { activeStatuses, useLookups, useUserRefs } from '@/features/lookups/api';
import { describeTemplate, useTemplates } from '@/features/templates/api';
import { useCurrentUser } from '@/lib/auth';
import { PREF_KEYS, readPref, usePersistedState, writePref } from '@/lib/prefs';

/** Where the dialog was opened from — the `new-card` event's payload. */
export interface NewCardTarget {
  projectId?: string | undefined;
  projectKey?: string | undefined;
  sectionId?: string | undefined;
}

/**
 * The project a mouse-less "new card" lands in when nothing else says. Same
 * `slate.ui.*` namespace as lib/prefs.ts; a device preference, not a setting.
 */
const LAST_PROJECT_KEY = 'slate.ui.last-project';
const ADD_ANOTHER_KEY = 'slate.ui.create-another';

const parseString = (raw: string): string => raw;
const parseBool = (raw: string): boolean | undefined =>
  raw === '1' ? true : raw === '0' ? false : undefined;

/**
 * Every control clears 44px where the pointer is coarse (§13.6) without
 * turning a laptop form into a wall of buttons.
 */
const FIELD =
  'border-input bg-background h-9 w-full rounded-md border px-2 text-sm [@media(pointer:coarse)]:h-11';
const INPUT = 'h-9 [@media(pointer:coarse)]:h-11';

export function NewCardDialog({
  open,
  onOpenChange,
  target,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target?: NewCardTarget;
}): JSX.Element {
  const me = useCurrentUser();
  const create = useCreateTask();
  const { data: projectPage } = useProjects('active');
  const { data: lookups } = useLookups();
  const { data: people = [] } = useUserRefs();

  const projects = useMemo(() => projectPage?.items ?? [], [projectPage]);

  /**
   * The starting project: what the caller asked for, else the last one used,
   * else — when there is only one — that one. Resolved once per opening; the
   * select owns it afterwards.
   */
  const [projectId, setProjectId] = useState<string>('');
  const resolved = useRef(false);
  useEffect(() => {
    if (!open) {
      resolved.current = false;
      return;
    }
    if (resolved.current || projects.length === 0) return;
    const byKey = target?.projectKey
      ? projects.find((project) => project.key === target.projectKey)?.id
      : undefined;
    const last = readPref(LAST_PROJECT_KEY, parseString);
    const remembered = projects.some((project) => project.id === last) ? last : undefined;
    const only = projects.length === 1 ? projects[0]?.id : undefined;
    setProjectId(target?.projectId ?? byKey ?? remembered ?? only ?? '');
    resolved.current = true;
  }, [open, projects, target?.projectId, target?.projectKey]);

  const project = projects.find((candidate) => candidate.id === projectId) ?? null;
  /**
   * #27 — the chosen project decides which rows this form shows and which it
   * insists on. Read from the project rather than a setting, so switching
   * project mid-draft re-shapes the form rather than leaving the last
   * project's rules on it.
   */
  const fields = project?.cardFields;
  const shows = (key: string): boolean => !isFieldHidden(fields, key);
  const needs = (key: string): boolean => isFieldRequired(fields, key);
  // Sections come with the project record, which the project page has already
  // cached — opening the dialog there costs nothing.
  const { data: projectDetail } = useProject(project?.key ?? null);
  const sections = projectDetail?.sections ?? [];

  // §6.3 — the section is remembered per project: someone filing bugs files
  // them in the same place all afternoon.
  const [sectionId, setSectionId] = usePersistedState<string>(
    `${PREF_KEYS.quickAddSection}.${project?.key ?? 'none'}`,
    '',
    parseString,
  );
  const [addAnother, setAddAnother] = usePersistedState<boolean>(
    ADD_ANOTHER_KEY,
    false,
    parseBool,
    (value) => (value ? '1' : '0'),
  );

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [priority, setPriority] = useState<Priority | ''>('');
  const [dueDate, setDueDate] = useState('');
  const [templateId, setTemplateId] = useState('');

  /**
   * What the project still wants — #27.
   *
   * Only the fields this dialog can actually collect: a project can require a
   * custom field the quick dialog does not render, and blocking Create on
   * something the form cannot offer would make the project impossible to file
   * into from here. The server still refuses it, and says which field.
   */
  const missing = (
    [
      ['description', description.trim() !== '', 'a description'],
      ['assignee', assigneeId !== '', 'an assignee'],
      ['dueDate', dueDate !== '', 'a due date'],
      ['priority', priority !== '', 'a priority'],
      ['section', sectionId !== '', 'a section'],
    ] as const
  )
    .filter(([key, provided]) => needs(key) && !provided)
    .map(([, , label]) => label);

  const titleRef = useRef<HTMLInputElement>(null);

  // The caller's section wins over the remembered one — only as the dialog
  // opens; the select owns it from then on.
  useEffect(() => {
    if (open && target?.sectionId) setSectionId(target.sectionId);
  }, [open, target?.sectionId, setSectionId]);

  // §6.16 — "as the title is typed … shows up to 5 'Similar cards'".
  const similar = useSimilarTasks(title, open);
  // §6.15 — this project's templates, plus the global ones.
  const templates = useTemplates(projectId || null);
  const template = (templates.data ?? []).find((item) => item.id === templateId);

  const statuses = activeStatuses(lookups);

  /** Set when closing because a card was created and is now open behind us. */
  const openedACard = useRef(false);

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (title.trim() === '' || create.isPending) return;
    if (projectId) writePref(LAST_PROJECT_KEY, projectId);

    create.mutate(
      {
        title: title.trim(),
        projectId: projectId || null,
        sectionId: sectionId || null,
        assigneeId: assigneeId || null,
        ...(statusId ? { statusId } : {}),
        priority: priority || null,
        dueDate: dueDate || null,
        description: description.trim() === '' ? null : textDoc(description.trim()),
        tagIds: [],
        collaboratorIds: [],
        templateId: templateId || null,
      },
      {
        onSuccess: () => {
          if (!addAnother) {
            // #23 — the new card's peek has just taken the caret into its
            // description. Radix restores focus to whatever opened the dialog
            // when it closes, which would take the caret straight back to the
            // "New card" button; this tells the close handler not to.
            openedACard.current = true;
            onOpenChange(false);
            return;
          }
          // Five in a row: what changes card-to-card is cleared, what stays the
          // same (project, section, assignee, status, due date) stays.
          setTitle('');
          setDescription('');
          setTemplateId('');
          titleRef.current?.focus();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92dvh] max-w-xl overflow-y-auto max-sm:h-dvh max-sm:max-h-none"
        onCloseAutoFocus={(event) => {
          if (!openedACard.current) return;
          openedACard.current = false;
          // `returnFocus` bows out on a prevented event, so the card panel
          // keeps the focus it just took (#23).
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>New card</DialogTitle>
          <DialogDescription>
            Only the title is required. Press Enter to create it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="card-title">Title</Label>
            <Input
              id="card-title"
              ref={titleRef}
              autoFocus
              required
              className={INPUT}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Login page rejects valid passwords"
            />
          </div>

          {(similar.data?.length ?? 0) > 0 ? (
            <div className="rounded-md border border-dashed p-2">
              <p className="text-muted-foreground mb-1 text-xs font-medium">
                Similar cards — is this a duplicate?
              </p>
              <ul className="space-y-0.5">
                {similar.data?.map((card) => (
                  <li key={card.id} className="truncate text-xs">
                    <span className="text-muted-foreground font-mono">#{card.number}</span>{' '}
                    {card.title}
                    <span className="text-muted-foreground"> · {card.statusName}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="card-project">Project</Label>
              <Combobox
                id="card-project"
                label="Project"
                className={FIELD}
                value={projectId}
                placeholder="(No project)"
                options={[
                  { value: '', label: '(No project)' },
                  ...projects.map((candidate) => ({
                    value: candidate.id,
                    label: candidate.name,
                    keywords: [candidate.key],
                  })),
                ]}
                onChange={setProjectId}
              />
            </div>

            {shows('section') ? (
              <div className="space-y-1.5">
                <Label htmlFor="card-section">Section{needs('section') ? ' *' : ''}</Label>
                <Combobox
                  id="card-section"
                  label="Section"
                  className={FIELD}
                  disabled={sections.length === 0}
                  value={sections.some((section) => section.id === sectionId) ? sectionId : ''}
                  placeholder="(No section)"
                  options={[
                    { value: '', label: '(No section)' },
                    ...sections.map((section) => ({ value: section.id, label: section.name })),
                  ]}
                  onChange={setSectionId}
                />
              </div>
            ) : null}

            {shows('assignee') ? (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="card-assignee">Assignee{needs('assignee') ? ' *' : ''}</Label>
                  {/* DAILY-15 — the support→dev handover starts here, not in the
                    panel four clicks later. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1.5 text-xs"
                    onClick={() => setAssigneeId(me.id)}
                  >
                    Assign to me
                  </Button>
                </div>
                {/* #19 — the list this feature was asked for. Forty colleagues
                  is a scroll in a native select and a two-keystroke filter
                  here, and the email disambiguates two people with one name
                  without widening the row. */}
                <Combobox
                  id="card-assignee"
                  label="Assignee"
                  className={FIELD}
                  value={assigneeId}
                  placeholder="Unassigned"
                  options={[
                    { value: '', label: 'Unassigned' },
                    ...people
                      .filter((person) => person.isActive || person.id === assigneeId)
                      .map((person) => ({
                        value: person.id,
                        label: person.name,
                        keywords: [person.email],
                        description: person.email,
                      })),
                  ]}
                  onChange={setAssigneeId}
                />
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="card-status">Status</Label>
              <Combobox
                id="card-status"
                label="Status"
                className={FIELD}
                value={statusId}
                placeholder="Default (backlog)"
                options={[
                  { value: '', label: 'Default (backlog)' },
                  ...statuses.map((status) => ({
                    value: status.id,
                    label: status.name,
                    keywords: [status.category],
                    icon: <ColorDot color={status.color} />,
                  })),
                ]}
                onChange={setStatusId}
              />
            </div>

            {shows('priority') ? (
              <div className="space-y-1.5">
                <Label htmlFor="card-priority">Priority{needs('priority') ? ' *' : ''}</Label>
                <Combobox
                  id="card-priority"
                  label="Priority"
                  className={`${FIELD} capitalize`}
                  value={priority}
                  placeholder="None"
                  options={[
                    { value: '', label: 'None' },
                    { value: 'low', label: 'Low' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'high', label: 'High' },
                    { value: 'urgent', label: 'Urgent' },
                  ]}
                  onChange={(value) => setPriority(value as Priority | '')}
                />
              </div>
            ) : null}

            {shows('dueDate') ? (
              <div className="space-y-1.5">
                <Label htmlFor="card-due">Due date{needs('dueDate') ? ' *' : ''}</Label>
                {/* A typo in the year is a card lost in 20024, so the input says
                  what a plausible year is. */}
                <Input
                  id="card-due"
                  type="date"
                  min="2000-01-01"
                  max="2100-12-31"
                  className={INPUT}
                  value={dueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                />
              </div>
            ) : null}
          </div>

          {shows('description') ? (
            <div className="space-y-1.5">
              <Label htmlFor="card-description">
                Description{needs('description') ? ' *' : ''}
              </Label>
              <textarea
                id="card-description"
                rows={3}
                className="border-input bg-background w-full rounded-md border p-2 text-sm"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Steps, links, what “done” looks like."
              />
            </div>
          ) : null}

          {(templates.data?.length ?? 0) > 0 ? (
            <div className="space-y-1.5">
              <Label htmlFor="card-template">Template</Label>
              <Combobox
                id="card-template"
                label="Template"
                className={FIELD}
                value={templateId}
                placeholder="No template"
                options={[
                  { value: '', label: 'No template' },
                  ...(templates.data ?? []).map((item) => ({
                    value: item.id,
                    label: item.name,
                    description: describeTemplate(item),
                  })),
                ]}
                onChange={(value) => {
                  setTemplateId(value);
                  const chosen = (templates.data ?? []).find((item) => item.id === value);
                  // A template's title is a starting point, not a lock: filling
                  // an empty field is helpful, overwriting typing is not.
                  if (chosen?.content.title && title.trim() === '') setTitle(chosen.content.title);
                }}
              />
              {template ? (
                <p className="text-muted-foreground text-xs">{describeTemplate(template)}</p>
              ) : null}
            </div>
          ) : null}

          <DialogFooter className="items-center gap-3 pt-1 sm:justify-between">
            {/* The label is the tap target, so it — not the 16px box — is what
                has to clear 44px on a touch screen (§13.6). */}
            <label className="flex items-center gap-2 text-sm [@media(pointer:coarse)]:min-h-11">
              <input
                type="checkbox"
                className="size-4 [@media(pointer:coarse)]:size-5"
                checked={addAnother}
                onChange={(event) => setAddAnother(event.target.checked)}
              />
              Create and add another
            </label>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={title.trim() === '' || missing.length > 0 || create.isPending}
                // The button says *what* is missing rather than only that
                // something is: a disabled control with no reason is the
                // thing people file tickets about.
                title={missing.length > 0 ? `Still needed: ${missing.join(', ')}` : undefined}
              >
                {create.isPending ? 'Creating…' : 'Create card'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
